import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, limit, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Activity, Wifi, WifiOff, RefreshCw, AlertTriangle, CheckCircle2, ShieldAlert, Server, Database, Clock } from 'lucide-react';
import { useAuth } from '../App';

interface SystemErrorLog {
  id: string;
  message: string;
  userEmail?: string;
  timestamp?: Timestamp | { seconds: number };
  url?: string;
}

export const HealthMonitorWidget: React.FC = () => {
  const { isAdmin, isManager } = useAuth();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [errors, setErrors] = useState<SystemErrorLog[]>([]);
  const [loadingErrors, setLoadingErrors] = useState(true);
  const [lastPingTime, setLastPingTime] = useState<Date>(new Date());
  const [pingStatus, setPingStatus] = useState<'healthy' | 'checking' | 'error'>('healthy');

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen to recent system errors from Firestore
    let unsubscribeErrors: (() => void) | null = null;
    try {
      const q = query(collection(db, 'system_errors'), orderBy('timestamp', 'desc'), limit(10));
      unsubscribeErrors = onSnapshot(q, (snapshot) => {
        const list = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as SystemErrorLog[];
        setErrors(list);
        setLoadingErrors(false);
      }, (err) => {
        console.warn('Failed to load system errors for health monitor:', err);
        setLoadingErrors(false);
      });
    } catch (e) {
      console.warn('System errors collection subscription error:', e);
      setLoadingErrors(false);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (unsubscribeErrors) unsubscribeErrors();
    };
  }, []);

  const handleTestPing = async () => {
    setPingStatus('checking');
    try {
      const start = Date.now();
      // Quick test query to users or metadata
      await onSnapshot(query(collection(db, 'users'), limit(1)), () => {});
      setLastPingTime(new Date());
      setPingStatus('healthy');
    } catch (err) {
      console.error('Firestore health ping failed:', err);
      setPingStatus('error');
    }
  };

  if (!isAdmin && !isManager) {
    return null;
  }

  return (
    <div className="bg-white rounded-[2rem] border border-slate-200/80 p-6 shadow-sm space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-slate-100 pb-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center shadow-inner">
            <Activity className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-black text-slate-900 tracking-tight">System Health & Sync Monitor</h3>
            <p className="text-xs font-medium text-slate-500">Real-time cloud connectivity and operational error telemetry</p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleTestPing}
          className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs flex items-center gap-2 transition-all cursor-pointer shadow-sm"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${pingStatus === 'checking' ? 'animate-spin' : ''}`} />
          Run Health Check
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Connectivity Status Card */}
        <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
          <div className="flex items-center justify-between text-slate-500 text-xs font-bold uppercase tracking-wider">
            <span>Network Status</span>
            {isOnline ? <Wifi className="w-4 h-4 text-emerald-600" /> : <WifiOff className="w-4 h-4 text-amber-600" />}
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
            <span className="text-base font-black text-slate-900">
              {isOnline ? 'Online (Cloud Connected)' : 'Offline (Local Cache Active)'}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 font-medium">
            {isOnline ? 'All Firestore bidirectional sync channels are active.' : 'Changes are stored in offline buffer and will sync upon reconnection.'}
          </p>
        </div>

        {/* Database Latency / Ping Status Card */}
        <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
          <div className="flex items-center justify-between text-slate-500 text-xs font-bold uppercase tracking-wider">
            <span>Firestore Engine</span>
            <Database className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${pingStatus === 'healthy' ? 'bg-emerald-500' : pingStatus === 'checking' ? 'bg-blue-500 animate-ping' : 'bg-rose-500'}`} />
            <span className="text-base font-black text-slate-900 capitalize">
              {pingStatus === 'healthy' ? 'Operational & Responsive' : pingStatus === 'checking' ? 'Testing connection...' : 'Connection Error'}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 font-medium">
            Last ping verified at {lastPingTime.toLocaleTimeString()}
          </p>
        </div>

        {/* Error Telemetry Summary Card */}
        <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
          <div className="flex items-center justify-between text-slate-500 text-xs font-bold uppercase tracking-wider">
            <span>Captured Exceptions</span>
            <ShieldAlert className="w-4 h-4 text-rose-600" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-black text-slate-900">{errors.length}</span>
            <span className="text-xs font-bold text-slate-500">logged in telemetry</span>
          </div>
          <p className="text-[11px] text-slate-500 font-medium">
            {errors.length === 0 ? 'Zero operational errors detected.' : 'Review recent error logs below for debugging.'}
          </p>
        </div>
      </div>

      {/* Recent System Errors List */}
      <div className="space-y-3 pt-2">
        <h4 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          Recent System Error Telemetry ({errors.length})
        </h4>

        {loadingErrors ? (
          <div className="p-8 text-center text-slate-400 text-xs font-medium">Loading error telemetry...</div>
        ) : errors.length === 0 ? (
          <div className="p-8 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200 text-center space-y-1">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto" />
            <p className="text-xs font-bold text-slate-700">No operational errors logged</p>
            <p className="text-[11px] text-slate-400">System is running at 100% operational integrity.</p>
          </div>
        ) : (
          <div className="space-Y-2 max-h-60 overflow-y-auto pr-1">
            {errors.map((err) => (
              <div key={err.id} className="p-3.5 bg-rose-50/50 border border-rose-100 rounded-2xl flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <p className="text-xs font-bold text-rose-900 break-words">{err.message}</p>
                  <div className="flex items-center gap-3 text-[10px] text-slate-500 font-medium">
                    <span>User: {err.userEmail || 'Anonymous'}</span>
                    {err.url && <span className="truncate max-w-xs">URL: {err.url}</span>}
                  </div>
                </div>
                <div className="text-[10px] text-slate-400 font-medium whitespace-nowrap flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {err.timestamp && 'seconds' in err.timestamp ? new Date(err.timestamp.seconds * 1000).toLocaleTimeString() : 'Just now'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
